import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useConnectionHub } from '@/catalog-apps/hooks/useConnectionHub'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { HubConnectionState } from '@microsoft/signalr'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ChatResponse,
  ChatContextState,
  SavedChatMeta,
  ChatContextEventName,
  getChatResponseRequestId,
  getContextEventFromResponse,
  getContextStateFromResponse,
  isChatStartedEnvelope,
  mapSavedSessionMetaToSavedChatMeta,
  mapServerChatSessionToChatSession,
  normalizeContextState,
} from './types'
import { STOP_CONFIRM_TIMEOUT_MS, TURN_LONG_WAIT_MS } from './chatTurnTiming'
import { useChatSession } from './useChatSession'

/** Bot type configurations (display names only - prompts/tools configured in worker) */
export const BOT_CONFIGS = [
  {
    label: 'General Assistant',
    value: 'plain',
    description: 'General assistant for any questions. I can help with features, troubleshooting, and more.',
  },
  {
    label: 'E-Commerce Assistant',
    value: 'e-commerce',
    description: 'Assistant for e-commerce questions. I can help with product information, pricing, and more.',
  },
  {
    label: 'Document Search Assistant',
    value: 'document-search',
    description: 'Assistant for document search questions. I can help with document information, search, and more.',
  },
]

/** Task names enum matching backend */
enum TaskNames {
  ExampleApp1 = 1,
  TestApp1 = 2,
  StructuredPdfProcessing = 3,
  LiveChat = 4,
}

/** Must match .NET CorrelationIdMiddleware (Tasks API uses this as the queue correlation id). */
const TASK_CORRELATION_ID_HEADER = 'X-Correlation-ID'

type CreateLiveChatTaskVariables = {
  requestId: string
  body: {
    projectId: number
    organisationId: number
    applicationKey: string
    taskName: TaskNames
    payload: Record<string, unknown>
    schemaVersion: string
  }
}

export interface ChatContextPayloadItem {
  selectionKey: string
  componentType: string
  componentId: string
  pageId?: string
  title?: string
  description?: string
  filters?: Record<string, unknown>[]
  capturedAt?: string
  responseData: unknown
  truncated?: boolean
}

export interface ChatSendPayload {
  message: string
  displayMessage?: string
  contextItems?: ChatContextPayloadItem[]
}

interface UseAiChatReturn {
  /** Unique chat ID based on user, app, and workspace */
  chatId: string
  /** Persistent chat history ID used by server-side storage */
  chatHistoryId: string
  // /** Context prefix for chat IDs (user:app:workspace) */
  // chatIdPrefix: string
  /** Whether the chat channel has been joined */
  isConnected: boolean
  /** Whether a message is currently being submitted */
  isSubmitting: boolean
  /** Whether a stop request was sent and we're waiting for the worker to confirm */
  isStoppingGeneration: boolean
  /** Whether cancel/stop is available for the in-flight turn */
  canStopGeneration: boolean
  /** Whether the worker emitted the `started` envelope for this turn */
  workerHasStarted: boolean
  /** Whether the in-flight turn has exceeded TURN_LONG_WAIT_MS (long-wait copy only) */
  showLongWaitHint: boolean
  /** Latest agent context window telemetry (SignalR + persisted session). */
  contextState: ChatContextState | null
  /** True while the worker is summarising earlier context before the turn runs. */
  isContextCompacting: boolean
  /** Brief banner after a successful compaction on the current turn. */
  showContextCompactedNotice: boolean
  /** Brief banner when compaction was skipped (e.g. no safe cut). */
  showContextSkippedNotice: boolean
  /** Most recent context-management SignalR event (for diagnostics). */
  lastContextEvent: ChatContextEventName | null
  /** Chat messages from localStorage */
  messages: ReturnType<typeof useChatSession>['messages']
  /** The type of bot to use for the chat */
  botType: string
  /** Set the type of bot to use for the chat */
  setBotType: (botType: string) => void
  /** Send a message to the chat */
  sendMessage: (payload: string | ChatSendPayload) => Promise<void>
  /** Stop the in-flight agent turn (kill switch) */
  stopGeneration: () => Promise<void>
  /** Clear all messages in current chat */
  clearMessages: () => void

  // Chat history management
  /** List of saved chats for this context */
  savedChats: SavedChatMeta[]
  /** Whether chat history list is being loaded/fetched */
  isHistoryLoading: boolean
  /** Whether a history session is being loaded */
  isLoadingHistoryChat: boolean
  /** Whether delete/clear history mutation is in progress */
  isHistoryMutating: boolean
  /** Save current chat to history */
  saveCurrentChat: () => void
  /** Load a chat from history */
  loadChat: (chatId: string) => Promise<void>
  /** Delete a specific chat from history */
  deleteChat: (chatId: string) => Promise<void>
  /** Clear all chat history for this context */
  clearAllHistory: () => Promise<void>
  /** Start a new chat */
  startNewChat: () => void
}

const AiChatContext = createContext<UseAiChatReturn | undefined>(undefined)

/**
 * Provider for managing AI chat functionality.
 * Handles SignalR connection, message sending/receiving, and session management.
 */
export function AiChatProvider({
  children,
  isOpen,
  isSheetOpen,
  fixedAgentKey,
}: {
  children: React.ReactNode
  isOpen?: boolean
  isSheetOpen?: boolean
  fixedAgentKey?: string
}) {
  const chatIsOpen = isOpen ?? isSheetOpen ?? true
  const { connection, reconnectAttempt } = useConnectionHub()
  // const { appKey, workspaceId, projectId, organisationId, appId } = useAppContext()
  const { appKey, projectId, organisationId, appId } = useAppContext()
  const [botType, setBotTypeState] = useState<string>(fixedAgentKey || 'plain')
  const {
    postTasksApiTasksCreate,
    getChatHistoryApiGetHistory,
    getChatHistoryApiGetSession,
    deleteChatHistoryApiDeleteSession,
    postChatHistoryApiBulkDeleteSessions,
  } = useRestClient()
  const inFlightRequestIdRef = useRef<string | null>(null)
  const workerHasStartedRef = useRef(false)
  // useMutation() only forwards { body } to requestFn; headers must be set on the
  // direct operation call (see openapi-qraft useMutation.js).
  const createLiveChatTask = useCallback(
    async (variables: CreateLiveChatTaskVariables) => {
      const result = await postTasksApiTasksCreate({
        body: variables.body,
        headers: {
          [TASK_CORRELATION_ID_HEADER]: variables.requestId,
        },
      } as Parameters<typeof postTasksApiTasksCreate>[0] & {
        headers: Record<string, string>
      })

      const confirmedRequestId = result.response?.headers.get(TASK_CORRELATION_ID_HEADER)
      if (confirmedRequestId) {
        inFlightRequestIdRef.current = confirmedRequestId
      }

      if (result.error) {
        throw result.error
      }

      return result.data
    },
    [postTasksApiTasksCreate]
  )
  // const [sub] = useSessionStorage('sub', '')

  // Generate context prefix (user:app:workspace) - used for grouping chats
  // const chatIdPrefix = `${sub}_${appKey}_${workspaceId}`
  // const [chatId, setChatId] = useState<string>(() => `${chatIdPrefix}_${Date.now()}`)
  const [chatId, setChatId] = useState<string>(() => crypto.randomUUID())
  const [chatHistoryId, setChatHistoryId] = useState<string>(() => crypto.randomUUID())
  const [isLoadingHistoryChat, setIsLoadingHistoryChat] = useState(false)
  const [historyInteractionAtById, setHistoryInteractionAtById] = useState<Record<string, number>>({})
  const wasSheetOpenRef = useRef(false)
  const shouldAutoLoadRecentRef = useRef(false)
  const shouldRefreshHistoryAfterResponseRef = useRef(false)
  const pendingNewChatHistoryIdRef = useRef<string | null>(null)
  const [lastActiveHistoryByContext, setLastActiveHistoryByContext] = useSessionStorage<Record<string, string>>(
    'ai-chat-last-active-history-by-context',
    {}
  )

  const historyPath = useMemo(
    () =>
      organisationId && projectId && appKey
        ? {
            organisationId,
            projectId,
            appKey,
          }
        : null,
    [organisationId, projectId, appKey]
  )
  const historyContextKey = useMemo(
    () =>
      historyPath
        ? [historyPath.organisationId, historyPath.projectId, historyPath.appKey, fixedAgentKey]
            .filter(Boolean)
            .join(':')
        : '',
    [fixedAgentKey, historyPath]
  )
  const lastActiveChatHistoryId = historyContextKey ? (lastActiveHistoryByContext[historyContextKey] ?? '') : ''
  const setBotType = useCallback(
    (nextBotType: string) => {
      if (fixedAgentKey) return
      setBotTypeState(nextBotType)
    },
    [fixedAgentKey]
  )

  useEffect(() => {
    if (!fixedAgentKey) return
    setBotTypeState(fixedAgentKey)
  }, [fixedAgentKey])

  const persistLastActiveChatHistoryId = useCallback(
    (nextChatHistoryId: string | null) => {
      if (!historyContextKey) return

      const nextMap = { ...lastActiveHistoryByContext }
      if (nextChatHistoryId) {
        nextMap[historyContextKey] = nextChatHistoryId
      } else {
        delete nextMap[historyContextKey]
      }
      setLastActiveHistoryByContext(nextMap)
    },
    [historyContextKey, lastActiveHistoryByContext, setLastActiveHistoryByContext]
  )
  const markHistoryInteraction = useCallback((targetChatHistoryId: string) => {
    setHistoryInteractionAtById((previous) => ({
      ...previous,
      [targetChatHistoryId]: Date.now(),
    }))
  }, [])

  const {
    data: historySessions = [],
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
  } = getChatHistoryApiGetHistory.useQuery(
    { path: historyPath ?? { organisationId: -1, projectId: -1, appKey: '' } },
    { enabled: Boolean(historyPath) }
  )

  const { mutateAsync: deleteHistorySession, isPending: isDeletingHistory } =
    deleteChatHistoryApiDeleteSession.useMutation()
  const { mutateAsync: bulkDeleteHistorySessions, isPending: isBulkDeletingHistory } =
    postChatHistoryApiBulkDeleteSessions.useMutation()

  const savedChats = useMemo<SavedChatMeta[]>(
    () =>
      historySessions
        .map(mapSavedSessionMetaToSavedChatMeta)
        .filter((chat) => Boolean(chat.id))
        .filter((chat) => !fixedAgentKey || chat.botType === fixedAgentKey)
        .sort((a, b) => {
          const aInteractedAt = historyInteractionAtById[a.id] ?? 0
          const bInteractedAt = historyInteractionAtById[b.id] ?? 0
          return Math.max(b.lastUpdatedAt, bInteractedAt) - Math.max(a.lastUpdatedAt, aInteractedAt)
        }),
    [fixedAgentKey, historyInteractionAtById, historySessions]
  )

  // Connection state
  const [chatChannelJoined, setChatChannelJoined] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isStoppingGeneration, setIsStoppingGeneration] = useState(false)
  const [canStopGeneration, setCanStopGeneration] = useState(false)
  const [workerHasStarted, setWorkerHasStarted] = useState(false)
  const [showLongWaitHint, setShowLongWaitHint] = useState(false)
  const [contextState, setContextState] = useState<ChatContextState | null>(null)
  const [isContextCompacting, setIsContextCompacting] = useState(false)
  const [showContextCompactedNotice, setShowContextCompactedNotice] = useState(false)
  const [showContextSkippedNotice, setShowContextSkippedNotice] = useState(false)
  const [lastContextEvent, setLastContextEvent] = useState<ChatContextEventName | null>(null)

  // Chat session with localStorage persistence
  const {
    messages,
    addUserMessageAndStartStreaming,
    appendToAssistantMessage,
    finalizeAssistantMessage,
    cancelStreamingTurn,
    discardStreamingAssistantMessage,
    clearMessages: clearSessionMessages,
    loadFromSession,
  } = useChatSession(chatId)

  const resetInFlightTurn = useCallback(() => {
    inFlightRequestIdRef.current = null
    workerHasStartedRef.current = false
    setWorkerHasStarted(false)
    setCanStopGeneration(false)
    setShowLongWaitHint(false)
    setIsStoppingGeneration(false)
    setIsSubmitting(false)
  }, [])

  const abortInFlightTurnLocally = useCallback(
    (
      title: string,
      description: string,
      options?: { rollbackUserMessage?: boolean; variant?: 'info' | 'error' }
    ) => {
      cancelStreamingTurn({ rollbackUserMessage: options?.rollbackUserMessage })
      resetInFlightTurn()

      if (options?.rollbackUserMessage) {
        shouldRefreshHistoryAfterResponseRef.current = false
        pendingNewChatHistoryIdRef.current = null
      }

      if (options?.variant === 'error') {
        toast.error(title, { description })
      } else {
        toast.info(title, { description })
      }
    },
    [cancelStreamingTurn, resetInFlightTurn]
  )

  // Save current chat to history
  const saveCurrentChat = useCallback(() => {
    toast.info('Chat sessions are saved server-side while you chat.')
  }, [])
  // Note:
  // We intentionally avoid message-driven history-list refetches here.
  // A previous debounced refetch (~1.2s after updates) caused delayed list refreshes
  // right after selecting history items, which felt like unnecessary churn.
  // Current refresh triggers are: sheet-open cycle and explicit history mutations.
  // If live sidebar metadata becomes important again, reintroduce a targeted refresh
  // that excludes selection-only flows.

  // Load a chat from history
  const loadChat = useCallback(
    async (targetChatHistoryId: string) => {
      if (!historyPath) return

      setIsLoadingHistoryChat(true)
      try {
        const sessionParameters = {
          path: {
            ...historyPath,
            chatHistoryId: targetChatHistoryId,
          },
        } as const

        // Force a fresh server read for selected history sessions.
        // fetchQuery can otherwise return cached data within staleTime.
        await getChatHistoryApiGetSession.invalidateQueries({
          parameters: sessionParameters,
        })

        const session = await getChatHistoryApiGetSession.fetchQuery({
          parameters: sessionParameters,
        })
        const mappedSession = mapServerChatSessionToChatSession(session, chatId)
        // Keep session state keyed by the current realtime chatId.
        // chatHistoryId identifies server persistence; chatId identifies local stream channel.
        loadFromSession({ ...mappedSession, chatId })
        setChatHistoryId(targetChatHistoryId)
        setContextState(normalizeContextState(session.contextState) ?? null)
        setIsContextCompacting(false)
        setShowContextCompactedNotice(false)
        setShowContextSkippedNotice(false)
        setLastContextEvent(null)
        persistLastActiveChatHistoryId(targetChatHistoryId)
        markHistoryInteraction(targetChatHistoryId)

        const chatMeta = savedChats.find((c) => c.id === targetChatHistoryId)
        if (chatMeta?.botType && !fixedAgentKey) {
          setBotTypeState(chatMeta.botType)
        }
        toast.success('Chat loaded')
      } catch (error) {
        console.error('[AiChat] Failed to load chat session:', error)
        toast.error('Failed to load chat')
      } finally {
        setIsLoadingHistoryChat(false)
      }
    },
    [
      chatId,
      getChatHistoryApiGetSession,
      historyPath,
      loadFromSession,
      markHistoryInteraction,
      persistLastActiveChatHistoryId,
      savedChats,
      fixedAgentKey,
    ]
  )

  // On each sheet-open cycle, fetch latest history and auto-load the newest chat once.
  useEffect(() => {
    if (!chatIsOpen) {
      wasSheetOpenRef.current = false
      return
    }

    if (!wasSheetOpenRef.current) {
      wasSheetOpenRef.current = true
      shouldAutoLoadRecentRef.current = true
    }
  }, [chatIsOpen])

  useEffect(() => {
    if (!chatIsOpen) return
    if (!shouldAutoLoadRecentRef.current) return
    if (isHistoryLoading || isHistoryFetching || isLoadingHistoryChat) return

    // Keep current in-memory conversation if one is already active.
    if (messages.length > 0) {
      shouldAutoLoadRecentRef.current = false
      return
    }

    const preferredChat =
      savedChats.find((chat) => chat.id === lastActiveChatHistoryId) ??
      // Fallback to most recent if previous session no longer exists
      savedChats[0]

    if (!preferredChat || preferredChat.id === chatHistoryId) {
      shouldAutoLoadRecentRef.current = false
      return
    }

    shouldAutoLoadRecentRef.current = false
    void loadChat(preferredChat.id)
  }, [
    chatHistoryId,
    isHistoryFetching,
    isHistoryLoading,
    isLoadingHistoryChat,
    chatIsOpen,
    lastActiveChatHistoryId,
    loadChat,
    messages.length,
    savedChats,
  ])

  // Delete a chat from server-side history
  const deleteChat = useCallback(
    async (targetChatHistoryId: string) => {
      if (!historyPath) return
      try {
        await deleteHistorySession({
          path: {
            ...historyPath,
            chatHistoryId: targetChatHistoryId,
          },
        })
        await getChatHistoryApiGetHistory.invalidateQueries(undefined)

        if (chatHistoryId === targetChatHistoryId) {
          setChatHistoryId(crypto.randomUUID())
          // setChatId(`${chatIdPrefix}:${Date.now()}`)
          setChatId(crypto.randomUUID())
          clearSessionMessages()
          persistLastActiveChatHistoryId(null)
        } else if (lastActiveChatHistoryId === targetChatHistoryId) {
          persistLastActiveChatHistoryId(null)
        }
        setHistoryInteractionAtById((previous) => {
          const next = { ...previous }
          delete next[targetChatHistoryId]
          return next
        })
        toast.success('Chat deleted')
      } catch (error) {
        console.error('[AiChat] Failed to delete chat session:', error)
        toast.error('Failed to delete chat')
      }
    },
    [
      chatHistoryId,
      clearSessionMessages,
      deleteHistorySession,
      getChatHistoryApiGetHistory,
      historyPath,
      lastActiveChatHistoryId,
      persistLastActiveChatHistoryId,
    ]
  )

  // Start a new chat
  const startNewChat = useCallback(() => {
    setChatHistoryId(crypto.randomUUID())
    // setChatId(`${chatIdPrefix}:${Date.now()}`)
    setChatId(crypto.randomUUID())
    clearSessionMessages()
    setContextState(null)
    setIsContextCompacting(false)
    setShowContextCompactedNotice(false)
    setShowContextSkippedNotice(false)
    setLastContextEvent(null)
    persistLastActiveChatHistoryId(null)
    toast.success('New chat started')
  }, [clearSessionMessages, persistLastActiveChatHistoryId])

  // Clear all chat history in this context
  const clearAllHistory = useCallback(async () => {
    if (!historyPath || savedChats.length === 0) return

    try {
      await bulkDeleteHistorySessions({
        path: historyPath,
        body: {
          chatHistoryIds: savedChats.map((chat) => chat.id),
        },
      })
      await getChatHistoryApiGetHistory.invalidateQueries(undefined)
      setHistoryInteractionAtById({})
      startNewChat()
      toast.success('Chat history cleared')
    } catch (error) {
      console.error('[AiChat] Failed to clear chat history:', error)
      toast.error('Failed to clear chat history')
    }
  }, [bulkDeleteHistorySessions, getChatHistoryApiGetHistory, historyPath, savedChats, startNewChat])

  // Clear messages in current chat
  const clearMessages = useCallback(() => {
    clearSessionMessages()
    setContextState(null)
    setIsContextCompacting(false)
    setShowContextCompactedNotice(false)
    setShowContextSkippedNotice(false)
    setLastContextEvent(null)
  }, [clearSessionMessages])

  // Handle receiving messages from SignalR
  const handleReceiveMessage = useCallback(
    (messageJson: string) => {
      try {
        const response: ChatResponse = JSON.parse(messageJson)

        const contextEvent = getContextEventFromResponse(response)
        if (contextEvent) {
          setLastContextEvent(contextEvent)

          const nextContextState = getContextStateFromResponse(response)
          if (nextContextState) {
            setContextState(nextContextState)
          }

          if (contextEvent === 'compacting') {
            setIsContextCompacting(true)
            setShowContextCompactedNotice(false)
            setShowContextSkippedNotice(false)
          } else if (contextEvent === 'compacted') {
            setIsContextCompacting(false)
            setShowContextCompactedNotice(true)
            setShowContextSkippedNotice(false)
          } else if (contextEvent === 'skipped') {
            setIsContextCompacting(false)
            setShowContextCompactedNotice(false)
            setShowContextSkippedNotice(true)
          } else {
            setIsContextCompacting(false)
          }

          return
        }

        // Contract: check error first; error messages should not become chat bubbles.
        if (response.error) {
          discardStreamingAssistantMessage()
          resetInFlightTurn()

          const isConfigurationError =
            response.error.includes('No API key resolved') || response.error.toLowerCase().includes('configuration')
          const userFacingMessage = isConfigurationError
            ? 'AI chat is misconfigured. Please contact support.'
            : 'Something went wrong while processing your message. The backend may retry automatically.'

          toast.error(userFacingMessage, {
            description: response.error,
          })
          return
        }

        if (isChatStartedEnvelope(response)) {
          const currentRequestId = inFlightRequestIdRef.current
          if (!currentRequestId) return

          const startedRequestId = getChatResponseRequestId(response)
          if (startedRequestId && startedRequestId !== currentRequestId) {
            return
          }

          workerHasStartedRef.current = true
          setWorkerHasStarted(true)
          setCanStopGeneration(true)
          return
        }

        if (response.done) {
          if (!inFlightRequestIdRef.current) return

          const cancelled = Boolean(response.cancelled)
          finalizeAssistantMessage(response.total_duration ?? response.totalDuration, cancelled)
          resetInFlightTurn()

          if (cancelled) {
            toast.warning('Agent stopped', {
              description: 'The response was cancelled. You can send a new message.',
            })
          }

          if (shouldRefreshHistoryAfterResponseRef.current && historyPath) {
            shouldRefreshHistoryAfterResponseRef.current = false
            const expectedChatHistoryId = pendingNewChatHistoryIdRef.current

            void (async () => {
              const retryDelaysMs = [500, 1500, 3000]
              for (const delayMs of retryDelaysMs) {
                await new Promise((resolve) => setTimeout(resolve, delayMs))
                await getChatHistoryApiGetHistory.invalidateQueries(undefined)
                const latestHistory = await getChatHistoryApiGetHistory.fetchQuery({
                  parameters: {
                    path: historyPath,
                  },
                })

                const isVisible =
                  !expectedChatHistoryId ||
                  latestHistory.some((session) => session.chatHistoryId === expectedChatHistoryId)
                if (isVisible) {
                  pendingNewChatHistoryIdRef.current = null
                  return
                }
              }
            })()
          }
          return
        }

        if (inFlightRequestIdRef.current) {
          appendToAssistantMessage(response)
        }
      } catch (error) {
        console.error('[AiChat] Failed to parse message:', error)
      }
    },
    [
      appendToAssistantMessage,
      discardStreamingAssistantMessage,
      finalizeAssistantMessage,
      getChatHistoryApiGetHistory,
      historyPath,
      resetInFlightTurn,
    ]
  )

  // Send a message to the chat
  const sendMessage = useCallback(
    async (payload: string | ChatSendPayload) => {
      const normalizedPayload: ChatSendPayload =
        typeof payload === 'string'
          ? {
              message: payload,
              displayMessage: payload,
              contextItems: [],
            }
          : payload

      const message = normalizedPayload.message
      const displayMessage = normalizedPayload.displayMessage ?? normalizedPayload.message
      const contextItems = normalizedPayload.contextItems ?? []

      if (!message.trim()) return
      if (connection.state !== HubConnectionState.Connected) {
        toast.error('Not connected to chat server')
        return
      }

      const requestId = crypto.randomUUID()
      inFlightRequestIdRef.current = requestId
      workerHasStartedRef.current = false
      setWorkerHasStarted(false)
      setCanStopGeneration(false)
      setShowLongWaitHint(false)
      setIsStoppingGeneration(false)
      setIsContextCompacting(false)
      setShowContextCompactedNotice(false)
      setShowContextSkippedNotice(false)
      setIsSubmitting(true)

      // Add user message and start assistant streaming in a single operation
      // This avoids race conditions with React's async state updates
      addUserMessageAndStartStreaming(displayMessage)
      markHistoryInteraction(chatHistoryId)
      if (!savedChats.some((chat) => chat.id === chatHistoryId)) {
        shouldRefreshHistoryAfterResponseRef.current = true
        pendingNewChatHistoryIdRef.current = chatHistoryId
      }

      // Create task to process the message
      const taskPayload: Record<string, unknown> = {
        ChatId: chatId,
        Message: message,
        AgentKey: botType,
        ChatHistoryId: chatHistoryId,
        AppKey: appKey || '',
        ApplicationId: appId,
      }
      if (contextItems.length > 0) {
        taskPayload.ContextItems = contextItems
      }

      void createLiveChatTask({
        requestId,
        body: {
          projectId: projectId!,
          organisationId: organisationId!,
          applicationKey: appKey || '',
          taskName: TaskNames.LiveChat,
          payload: taskPayload,
          schemaVersion: '1',
        },
      }).catch((error) => {
        console.error('[AiChat] Failed to send message:', error)
        abortInFlightTurnLocally(
          'Failed to send message',
          'Your message could not be queued. It was cleared so you can try again.',
          { rollbackUserMessage: true, variant: 'error' }
        )
      })
    },
    [
      connection.state,
      addUserMessageAndStartStreaming,
      abortInFlightTurnLocally,
      createLiveChatTask,
      projectId,
      organisationId,
      appKey,
      chatId,
      chatHistoryId,
      botType,
      appId,
      markHistoryInteraction,
      savedChats,
    ]
  )

  const stopGeneration = useCallback(async () => {
    const requestId = inFlightRequestIdRef.current
    if (!isSubmitting || !requestId || !canStopGeneration) return

    if (!workerHasStartedRef.current) {
      abortInFlightTurnLocally(
        'Request cancelled',
        'The agent did not pick up this message. Your message was cleared so you can try again.',
        { rollbackUserMessage: true }
      )
      return
    }

    if (connection.state !== HubConnectionState.Connected) {
      abortInFlightTurnLocally(
        'Chat reset locally',
        'Lost connection to the chat server before the stop could be confirmed.'
      )
      return
    }

    try {
      await connection.invoke(
        'SendMessageToChat',
        chatId,
        JSON.stringify({
          type: 'cancel',
          requestId,
        })
      )
      setIsStoppingGeneration(true)
      toast.info('Stopping agent…', {
        description: 'Waiting for the current step to finish.',
      })
    } catch (error) {
      console.error('[AiChat] Failed to send cancel request:', error)
      abortInFlightTurnLocally(
        'Chat reset locally',
        'Could not send a stop signal to the agent. The chat has been reset.'
      )
    }
  }, [abortInFlightTurnLocally, canStopGeneration, chatId, connection, isSubmitting])

  // Long-wait copy in the pending bubble — always tied to TURN_LONG_WAIT_MS, not `started`.
  useEffect(() => {
    if (!isSubmitting) return

    const timeoutId = window.setTimeout(() => {
      setShowLongWaitHint(true)
    }, TURN_LONG_WAIT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isSubmitting])

  // Enable cancel/stop when the worker has not connected within a reasonable window.
  useEffect(() => {
    if (!isSubmitting || canStopGeneration) return

    const timeoutId = window.setTimeout(() => {
      setCanStopGeneration(true)
    }, TURN_LONG_WAIT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [canStopGeneration, isSubmitting])

  // Auto-dismiss context feedback banners after a short beat.
  useEffect(() => {
    if (!showContextCompactedNotice && !showContextSkippedNotice) return

    const timeoutId = window.setTimeout(() => {
      setShowContextCompactedNotice(false)
      setShowContextSkippedNotice(false)
    }, 8000)

    return () => window.clearTimeout(timeoutId)
  }, [showContextCompactedNotice, showContextSkippedNotice])

  // If the worker never confirms a SignalR stop, reset the chat locally.
  useEffect(() => {
    if (!isStoppingGeneration) return

    const timeoutId = window.setTimeout(() => {
      abortInFlightTurnLocally(
        'Chat reset locally',
        'The agent did not confirm the stop in time. You can send a new message.'
      )
    }, STOP_CONFIRM_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [abortInFlightTurnLocally, isStoppingGeneration])

  // Join chat channel and set up message listener
  useEffect(() => {
    if (!chatIsOpen) {
      setChatChannelJoined(null)
      return
    }

    let isActive = true

    const joinChatChannel = async () => {
      try {
        await connection.invoke('JoinChatChannel', chatId, 'frontend')
        if (!isActive) return
        setChatChannelJoined(chatId)
        console.info(`[AiChat] Joined chat channel: ${chatId}`)
      } catch (error) {
        if (!isActive) return
        console.error('[AiChat] Failed to join chat channel:', error)
        toast.error(`Failed to join chat channel`)
        setChatChannelJoined(null)
      }
    }

    if (connection.state === HubConnectionState.Connected) {
      joinChatChannel()
      connection.on('ReceiveMessage', handleReceiveMessage)
    }

    return () => {
      isActive = false
      connection.off('ReceiveMessage', handleReceiveMessage)
    }
  }, [connection, chatId, handleReceiveMessage, chatIsOpen])

  // Re-join channel on reconnection
  useEffect(() => {
    if (chatIsOpen && reconnectAttempt > 0 && connection.state === HubConnectionState.Connected) {
      connection.invoke('JoinChatChannel', chatId, 'frontend').catch((error) => {
        console.error('[AiChat] Failed to rejoin chat channel:', error)
      })
    }
  }, [reconnectAttempt, connection, chatId, chatIsOpen])

  const isHistoryMutating = isDeletingHistory || isBulkDeletingHistory

  const contextValue = useMemo(
    () => ({
      chatId,
      chatHistoryId,
      isConnected: chatChannelJoined === chatId,
      isSubmitting,
      isStoppingGeneration,
      canStopGeneration,
      workerHasStarted,
      showLongWaitHint,
      contextState,
      isContextCompacting,
      showContextCompactedNotice,
      showContextSkippedNotice,
      lastContextEvent,
      messages,
      botType,
      setBotType,
      sendMessage,
      stopGeneration,
      clearMessages,
      // Chat history
      savedChats,
      isHistoryLoading: isHistoryLoading || isHistoryFetching,
      isLoadingHistoryChat,
      isHistoryMutating,
      saveCurrentChat,
      loadChat,
      deleteChat,
      clearAllHistory,
      startNewChat,
    }),
    [
      botType,
      chatChannelJoined,
      chatId,
      chatHistoryId,
      clearAllHistory,
      clearMessages,
      deleteChat,
      isSubmitting,
      isStoppingGeneration,
      canStopGeneration,
      workerHasStarted,
      showLongWaitHint,
      contextState,
      isContextCompacting,
      showContextCompactedNotice,
      showContextSkippedNotice,
      lastContextEvent,
      isHistoryFetching,
      isHistoryLoading,
      isHistoryMutating,
      isLoadingHistoryChat,
      loadChat,
      messages,
      saveCurrentChat,
      savedChats,
      sendMessage,
      stopGeneration,
      setBotType,
      startNewChat,
    ]
  )

  return <AiChatContext.Provider value={contextValue}>{children}</AiChatContext.Provider>
}

/**
 * Custom hook for accessing AI chat functionality.
 * Throws an error if used outside of an AiChatProvider.
 */
export function useAiChat(): UseAiChatReturn {
  const context = useContext(AiChatContext)
  if (!context) {
    throw new Error('useAiChat must be used within an AiChatProvider')
  }
  return context
}
